import { mapMaybe } from '@execonline-inc/collections';
import { warnAndNotify } from '@execonline-inc/error-reporting.private';
import { toTask } from '@execonline-inc/maybe-adapter';
import { noop } from '@kofno/piper';
import { just, nothing } from 'maybeasy';
import { Task } from 'taskarian';
import {
  CaptionSourceFile,
  PlayerOptionsError,
  StreamMissing,
  ThumbnailPreviewMissingError,
  VideoSourceFile,
  VideoSourceFileLabel,
  streamMissing,
  thumbnailPreviewMissingError,
} from '../JWPlayer/Types';
import { findLinkBy, findLinkByT } from '../Links';
import { Link } from '../Resource/Types';
import {
  ReqHlsVideoAsset,
  ReqHlsVideoAssetResource,
  SelectedPlayer,
  Subtitle,
  SubtitleResource,
  VideoSelectionFailure,
  autoLoadIndexT,
  missingProgressiveLow,
  progressivePlayer,
} from './Types';

const getProgressiveStream = (
  reqHlsVideoAsset: ReqHlsVideoAsset,
  label: VideoSourceFileLabel,
): Task<PlayerOptionsError, VideoSourceFile> => {
  return new Task<StreamMissing, VideoSourceFile>((reject, resolve) => {
    switch (label) {
      case 'Low':
        findLinkBy({ rel: 'view' }, reqHlsVideoAsset.lowProgressiveStream.links)
          .map((lowProgressiveStreamLink: Link) =>
            resolve({
              file: lowProgressiveStreamLink.href,
              label: label,
              default: reqHlsVideoAsset.lowProgressiveStream.payload.default ? 'true' : 'false',
            }),
          )
          .getOrElse(() => reject(streamMissing(`Low Stream is missing!`)));
        break;
      case 'Medium':
        findLinkBy({ rel: 'view' }, reqHlsVideoAsset.mediumProgressiveStream.links)
          .map((mediumProgressiveStreamLink: Link) =>
            resolve({
              file: mediumProgressiveStreamLink.href,
              label: label,
              default: reqHlsVideoAsset.mediumProgressiveStream.payload.default ? 'true' : 'false',
            }),
          )
          .getOrElse(() => reject(streamMissing(`Medium Stream is missing!`)));
        break;
      case 'High':
        findLinkBy({ rel: 'view' }, reqHlsVideoAsset.mediumProgressiveStream.links)
          .map((highProgressiveStreamLink: Link) =>
            resolve({
              file: highProgressiveStreamLink.href,
              label: label,
              default: reqHlsVideoAsset.mediumProgressiveStream.payload.default ? 'true' : 'false',
            }),
          )
          .getOrElse(() => reject(streamMissing(`High Stream is missing!`)));

        break;
    }
    return noop;
  });
};

export const fallbackProgressiveSources = (
  reqHlsVideoAsset: ReqHlsVideoAsset,
): Task<never, VideoSourceFile[]> =>
  Task.succeed({})
    .assign('low', getProgressiveStream(reqHlsVideoAsset, 'Low'))
    .assign('med', getProgressiveStream(reqHlsVideoAsset, 'Medium'))
    .assign('high', getProgressiveStream(reqHlsVideoAsset, 'High'))
    .map(({ low, med, high }) => [low, med, high])
    .orElse(() => {
      warnAndNotify('MissingFallbackSources', 'Fallback sources failed to load', {
        videoAssetUuid: reqHlsVideoAsset.uuid,
      });
      return Task.succeed([]);
    });

export const subtitles = (
  reqHlsVideoAsset: ReqHlsVideoAsset,
): Task<never, ReadonlyArray<CaptionSourceFile>> => {
  return new Task<never, ReadonlyArray<CaptionSourceFile>>((_, resolve) => {
    const cs = mapMaybe((subtitle: SubtitleResource) => {
      return findLinkBy({ rel: 'view' }, subtitle.links)
        .orElse(() => {
          warnAndNotify('MissingACaption', "We're captions for this video", {
            videoResourceId: reqHlsVideoAsset.uuid,
          });
          return nothing();
        })
        .map((subtitleLink: Link): CaptionSourceFile => {
          return {
            kind: 'captions',
            file: subtitleLink.href,
            label: subtitle.payload.label,
          };
        });
    }, reqHlsVideoAsset.subtitles);
    resolve(cs);
    return noop;
  });
};

export const thumbnail = (links: ReadonlyArray<Link>): Task<ThumbnailPreviewMissingError, string> =>
  findLinkByT({ rel: 'thumbnail' }, links)
    .map(({ href }) => href)
    .mapError((_) => thumbnailPreviewMissingError(`Warning: thumbnail preview image is missing`));

export const sprite = (resource: ReqHlsVideoAssetResource): Task<never, string> =>
  findLinkByT({ rel: 'sprite' }, resource.links)
    .map(({ href }) => href)
    .orElse((_) => {
      warnAndNotify('MissingVideoSprite', 'Sprite preview missing; video still loaded', {
        videoAssetUuid: resource.payload.uuid,
      });
      return Task.succeed('');
    });

export const progressiveSources = (
  reqHlsVideoAsset: ReqHlsVideoAsset,
): Task<VideoSelectionFailure[], SelectedPlayer> =>
  Task.succeed<VideoSelectionFailure[], {}>({})
    .assign('low', progressiveSource(reqHlsVideoAsset, 'Low'))
    .assign('med', progressiveSource(reqHlsVideoAsset, 'Medium'))
    .assign('high', progressiveSource(reqHlsVideoAsset, 'High'))
    .map(({ low, med, high }) => [low, med, high])
    .map(progressivePlayer);

const reqHlsVideoAssetSubtitles = (asset: ReqHlsVideoAsset): ReadonlyArray<Subtitle> =>
  asset.subtitles.map((s) => s.payload);

export const autoLoadIndex = autoLoadIndexT(reqHlsVideoAssetSubtitles);

function createVideoSourceFile(
  link: { href: string },
  payload: { default: boolean },
  label: VideoSourceFileLabel,
): VideoSourceFile {
  return {
    file: link.href,
    label: label,
    default: payload.default ? 'true' : 'false',
  };
}

const getVideoSourceFileTask = (
  reqHlsVideoAsset: ReqHlsVideoAsset,
): Task<VideoSelectionFailure[], { link: { href: string }; payload: { default: boolean } }> => {
  const lowProgressiveStream = reqHlsVideoAsset.lowProgressiveStream;
  return toTask(
    [missingProgressiveLow()],
    just(lowProgressiveStream).assign('link', ({ links }) =>
      findLinkBy({ rel: 'view' }, reqHlsVideoAsset.lowProgressiveStream.links),
    ),
  );
};

export const progressiveSource = (
  reqHlsVideoAsset: ReqHlsVideoAsset,
  label: VideoSourceFileLabel,
): Task<VideoSelectionFailure[], VideoSourceFile> =>
  getVideoSourceFileTask(reqHlsVideoAsset).map(({ link, payload }) =>
    createVideoSourceFile(link, payload, label),
  );

export const lowProgressiveSources = (
  reqHlsVideoAsset: ReqHlsVideoAsset,
): Task<VideoSelectionFailure[], VideoSourceFile[]> =>
  getVideoSourceFileTask(reqHlsVideoAsset).map(({ link, payload }) => [
    createVideoSourceFile(link, payload, 'Low'),
  ]);
